import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { EMPTY, Subscription, merge } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

import { MediaLibraryService, UploadProgress, AnalysisProgressState } from 'src/app/gen2ai/media-library.service';
import { MediaCategory, MediaItem, MediaType } from 'src/typings/media.interface';
import { EditMediaDialogComponent, EditMediaDialogResult } from '../dialogs/edit-media-dialog/edit-media-dialog.component';

type MediaTypeFilter = MediaType | 'all';

type AnalysisPanelState = 'idle' | 'uploading' | 'processing' | 'ready' | 'failed';

@Component({
  selector: 'app-media-library',
  templateUrl: './media-library.component.html',
  styleUrls: ['./media-library.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class MediaLibraryComponent implements OnInit, OnDestroy {
  @Output() insertIntoChat = new EventEmitter<string>();
  @Output() insertMediaIntoChat = new EventEmitter<MediaItem>();

  @ViewChild('fileUploadInput') fileUploadInput?: ElementRef<HTMLInputElement>;

  // Forms
  uploadForm: FormGroup;
  searchForm: FormGroup;

  private allMedia: MediaItem[] = [];

  pinnedMedia: MediaItem[] = [];
  otherMedia: MediaItem[] = [];

  categories: MediaCategory[] = [];
  selectedCategory: MediaCategory | null = null;
  selectedMediaType: MediaTypeFilter = 'all';
  showArchived = false;

  isUploading = false;
  isAnalyzing = false;
  uploadProgress = 0;

  selectedFiles: File[] = [];

  analysisPanel: {
    visible: boolean;
    state: AnalysisPanelState;
    title: string;
    message: string;
    progress: number;
    mediaIds: string[];
  } = {
    visible: false,
    state: 'idle',
    title: '',
    message: '',
    progress: 0,
    mediaIds: []
  };

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private mediaService: MediaLibraryService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    this.uploadForm = this.fb.group({
      title: [''],
      category: ['']
    });

    this.searchForm = this.fb.group({
      query: ['']
    });
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mediaService.items$.subscribe(media => {
        this.allMedia = media || [];
        this.rebuildListsAndFilters();
        this.cdr.detectChanges();
      })
    );

    void this.mediaService.loadAllMediaForUser();

    this.subscriptions.add(
      this.searchForm.get('query')!.valueChanges.subscribe(() => {
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.mediaService.stopAllAnalysisPolling();
  }

  // ---------------------------------------------------------------------------
  // Attach
  // ---------------------------------------------------------------------------

  attachMedia(): void {
    const input = this.fileUploadInput?.nativeElement;
    if (!input) return;
    input.value = '';
    input.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    if (!files.length) return;

    this.selectedFiles = files;
    input.value = '';
    this.cdr.detectChanges();
  }

  clearSelectedFiles(): void {
    this.selectedFiles = [];
    const input = this.fileUploadInput?.nativeElement;
    if (input) input.value = '';
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Upload + Analysis Workflow
  // ---------------------------------------------------------------------------

  async uploadMedia(): Promise<void> {
    if (!this.selectedFiles.length || this.isUploading || this.isAnalyzing) return;

    this.isUploading = true;
    this.uploadProgress = 0;

    // Prep panel
    this.analysisPanel.visible = true;
    this.analysisPanel.state = 'uploading';
    this.analysisPanel.title = 'Uploading…';
    this.analysisPanel.message = 'Uploading media to storage…';
    this.analysisPanel.progress = 0;
    this.analysisPanel.mediaIds = [];

    const formVal = this.uploadForm.value;
    const metadataBase = {
      title: String(formVal.title || '').trim(),
      category: (formVal.category || '') as MediaCategory
      // Note: labels removed - not supported by backend
    };

    const uploadedMediaIds: string[] = [];

    for (let i = 0; i < this.selectedFiles.length; i++) {
      const file = this.selectedFiles[i];

      try {
        const metadata = {
          ...metadataBase,
          title: metadataBase.title || file.name.replace(/\.[^/.]+$/, '')
        };

        const mediaId = await new Promise<string | null>((resolve) => {
          this.mediaService.uploadMediaWithProgress(file, metadata).subscribe({
            next: (progress: UploadProgress) => {
              if (progress.status === 'progress') {
                // overall upload progress for the batch
                const fileWeight = 100 / this.selectedFiles.length;
                const completedWeight = i * fileWeight;
                const currentFileContribution = (progress.percentage / 100) * fileWeight;

                this.uploadProgress = Math.round(completedWeight + currentFileContribution);
                this.analysisPanel.progress = this.uploadProgress;
                this.cdr.detectChanges();
              } else if (progress.status === 'complete') {
                resolve(progress.mediaId || null);
              }
            },
            error: (err) => {
              console.error(`Upload failed for ${file.name}`, err);
              resolve(null);
            }
          });
        });

        if (mediaId) uploadedMediaIds.push(mediaId);

      } catch (err) {
        console.error(err);
      }
    }

    this.isUploading = false;
    this.uploadProgress = 0;

    // Clear attach UI regardless (the file is saved if upload succeeded)
    this.clearSelectedFiles();
    this.uploadForm.reset();

    if (!uploadedMediaIds.length) {
      this.analysisPanel.state = 'failed';
      this.analysisPanel.title = 'Upload failed';
      this.analysisPanel.message = 'No files were uploaded. Please try again.';
      this.analysisPanel.progress = 0;
      this.cdr.detectChanges();
      return;
    }

    void this.mediaService.loadAllMediaForUser();

    // ✅ Now: inform saved + analysis starting
    this.analysisPanel.state = 'processing';
    this.analysisPanel.title = 'Media saved';
    this.analysisPanel.message =
      'Your media was successfully uploaded to storage and is being analyzed to generate a caption, description, and category.';
    this.analysisPanel.progress = 5;
    this.analysisPanel.mediaIds = uploadedMediaIds;

    this.isAnalyzing = true;
    this.cdr.detectChanges();

    // Firestore `media/{jobId}` updates + HTTP pollMediaJob (resumeenhancer main.py): video jobs only advance when poll runs.
    const analysis$ = merge(
      this.mediaService.watchAnalysisForJobIds(uploadedMediaIds).pipe(
        catchError((err) => {
          console.warn('Media library: Firestore watch unavailable, using HTTP poll only.', err);
          return EMPTY;
        })
      ),
      this.mediaService.pollAnalysisForJobIds(uploadedMediaIds, 2000)
    );

    const onAnalysisState = (state: AnalysisProgressState) => {
      this.analysisPanel.progress = state.progress;

      if (state.state === 'processing') {
        this.analysisPanel.state = 'processing';
        this.analysisPanel.title = 'Analyzing…';
        this.analysisPanel.message =
          'Gemini Vision / Video Intelligence is analyzing your media (server poll + live updates)…';
      }

      if (state.state === 'ready') {
        this.isAnalyzing = false;
        this.analysisPanel.state = 'ready';
        this.analysisPanel.title = 'Analysis complete';
        this.analysisPanel.message = 'Captions, descriptions, and categories are now available in your library.';
        this.analysisPanel.progress = 100;
        void this.mediaService.loadAllMediaForUser();
      }

      if (state.state === 'failed') {
        this.isAnalyzing = false;
        this.analysisPanel.state = 'failed';
        this.analysisPanel.title = 'Analysis finished (issues)';
        this.analysisPanel.message =
          state.ready > 0
            ? 'Some items could not be fully analyzed, but media is saved. You can edit details or retry later.'
            : 'The media was saved, but analysis reported an error. You can edit category/description manually or retry later.';
        this.analysisPanel.progress = 100;
        void this.mediaService.loadAllMediaForUser();
      }

      this.cdr.detectChanges();
    };

    this.subscriptions.add(
      analysis$.subscribe({
        next: onAnalysisState,
        error: (err) => {
          console.error('Analysis pipeline error', err);
          this.isAnalyzing = false;
          this.analysisPanel.state = 'failed';
          this.analysisPanel.title = 'Analysis status unavailable';
          this.analysisPanel.message =
            'Media should still appear in your library. Refresh the page or open the library again if you do not see it.';
          this.analysisPanel.progress = 100;
          void this.mediaService.loadAllMediaForUser();
          this.cdr.detectChanges();
        }
      })
    );
  }

  dismissAnalysisPanel(): void {
    this.analysisPanel.visible = false;
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Filter & List Management
  // ---------------------------------------------------------------------------

  private rebuildListsAndFilters(): void {
    const base = this.showArchived
      ? this.allMedia.filter(m => !!m.archived)
      : this.allMedia.filter(m => !m.archived);

    this.pinnedMedia = base.filter(m => !!m.pinned);
    this.otherMedia = base.filter(m => !m.pinned);

    const categorySet = new Set<MediaCategory>();
    this.allMedia.forEach(m => { if (m.category) categorySet.add(m.category); });
    this.categories = Array.from(categorySet);
  }

  get filteredPinned(): MediaItem[] { return this.applyFilters(this.pinnedMedia); }
  get filteredOthers(): MediaItem[] { return this.applyFilters(this.otherMedia); }

  private applyFilters(list: MediaItem[]): MediaItem[] {
    let filtered = list || [];

    if (this.selectedMediaType !== 'all') {
      filtered = filtered.filter(m => m.mediaType === this.selectedMediaType);
    }

    if (this.selectedCategory) {
      filtered = filtered.filter(m => m.category === this.selectedCategory);
    }

    const q = String(this.searchForm.get('query')?.value || '').toLowerCase().trim();
    if (q) {
      filtered = filtered.filter(m =>
        (m.title || '').toLowerCase().includes(q) ||
        (m.description || '').toLowerCase().includes(q) ||
        (m.caption || '').toLowerCase().includes(q) ||
        (m.category || '').toLowerCase().includes(q) ||
        (m.fileName || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }

  selectCategory(category: MediaCategory | null): void {
    this.selectedCategory = category;
    this.cdr.detectChanges();
  }

  selectMediaType(type: MediaTypeFilter): void {
    this.selectedMediaType = type;
    this.cdr.detectChanges();
  }

  toggleArchivedView(): void {
    this.showArchived = !this.showArchived;
    this.rebuildListsAndFilters();
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Drag & Drop
  // ---------------------------------------------------------------------------

  async dropPinned(event: CdkDragDrop<MediaItem[]>): Promise<void> {
    const item = event.item.data as MediaItem;
    if (item && !item.pinned) {
      await this.mediaService.togglePinned(item.id, true);
    }
  }

  async dropOthers(event: CdkDragDrop<MediaItem[]>): Promise<void> {
    const item = event.item.data as MediaItem;
    if (item && item.pinned) {
      await this.mediaService.togglePinned(item.id, false);
    }
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  async togglePinned(media: MediaItem, pinned: boolean): Promise<void> {
    await this.mediaService.togglePinned(media.id, pinned);
  }

  async archive(media: MediaItem): Promise<void> {
    await this.mediaService.toggleArchived(media.id, !media.archived);
  }

  async delete(media: MediaItem): Promise<void> {
    if (confirm('Delete this item permanently?')) {
      await this.mediaService.deleteMediaItem(media.id);
    }
  }

  async editMedia(media: MediaItem): Promise<void> {
    const dialogRef = this.dialog.open(EditMediaDialogComponent, {
      width: '600px',
      data: { media, availableCategories: this.categories }
    });

    dialogRef.afterClosed().subscribe(async (result: EditMediaDialogResult) => {
      if (result) {
        await this.mediaService.updateMediaItem(media.id, result);
      }
    });
  }

  useInChat(media: MediaItem): void {
    this.insertMediaIntoChat.emit(media);
  }

  downloadMedia(media: MediaItem): void {
    const link = document.createElement('a');
    link.href = media.mediaUrl;
    link.download = media.fileName || 'download';
    link.target = '_blank';
    link.click();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  getMediaIcon(type: MediaType): string {
    const icons: Record<string, string> = { image: 'image', video: 'videocam', audio: 'audiotrack' };
    return icons[type] || 'insert_drive_file';
  }

  detectFileType(mimeType: string): MediaType {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'unknown' as MediaType;
  }

  getSafeUrl(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  getThumbnailUrl(media: MediaItem): string {
    return media.thumbnailUrl || media.mediaUrl || '';
  }

  isMediaProcessing(media: MediaItem): boolean {
    return media.status === 'processing';
  }

  isMediaFailed(media: MediaItem): boolean {
    return media.status === 'failed';
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  formatDate(dateString: any): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  handleImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/img-placeholder.png';
  }

  trackById(_i: number, item: MediaItem): string {
    return item.id;
  }
}